<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import AppDataTable from "@/components/AppDataTable.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import { useResourceList } from "@/composables/useResourceList";
import { formatDate, toDate } from "@/lib/date";
import { fetchMessages, fetchMessageReadNames, deleteMessage } from "./api";

const router = useRouter();
const columns = [
  { key: "title",     label: "Title",      sortable: true },
  { key: "startDate", label: "Start Date" },
  { key: "endDate",   label: "End Date" },
  { key: "actions",   label: "Actions",    align: "end", width: "300px" },
];

const rawItems = ref([]);
const { rows, loading, error, pendingDelete, deleting, load, askDelete, confirmDelete } =
  useResourceList({
    fetch: () => fetchMessages().then((data) => { rawItems.value = Array.isArray(data) ? data : (data?.data ?? []); return data; }),
    remove: deleteMessage,
    mapRow: (m) => ({ id: m._id, title: m.title, startDate: formatDate(m.startDate), endDate: formatDate(m.endDate), _sortDate: toDate(m.updatedAt ?? m.startDate)?.getTime() ?? 0 }),
    sort: (a, b) => b._sortDate - a._sortDate,
  });

// ── View dialog ──────────────────────────────────────────────────────────────
const viewDialog = ref(false);
const viewItem = ref(null);
function openView(row) {
  viewItem.value = rawItems.value.find((m) => m._id === row.id) ?? null;
  viewDialog.value = true;
}
function openExternalUrl(url) {
  if (!url) return;
  window.open(url.startsWith("http") ? url : "http://" + url, "_blank");
}

// ── Analytics dialog ─────────────────────────────────────────────────────────
const analyticsDialog = ref(false);
const analyticsItem = ref(null);
const analyticsReadList = ref([]);
const analyticsLoading = ref(false);
async function openAnalytics(row) {
  analyticsDialog.value = true;
  analyticsLoading.value = true;
  analyticsItem.value = rawItems.value.find((m) => m._id === row.id) ?? null;
  analyticsReadList.value = [];
  try {
    const names = await fetchMessageReadNames(row.id).catch(() => []);
    analyticsReadList.value = Array.isArray(names) ? names : [];
  } finally { analyticsLoading.value = false; }
}

function countReads(item) {
  if (!item?.msgContent) return 0;
  const content = Object.values(item.msgContent);
  return content.filter((v) => String(v).includes("read")).length;
}
</script>

<template>
  <section>
    <div class="d-flex align-center justify-space-between mb-4 gap-3 flex-wrap">
      <h1 class="text-h5">Manage Messages</h1>
      <div class="d-flex gap-2 flex-wrap">
        <VBtn variant="tonal" @click="load">Refresh</VBtn>
        <VBtn variant="tonal" @click="router.push({ name: 'messages-templates-new' })">Create Template</VBtn>
        <VBtn variant="tonal" @click="router.push({ name: 'messages-qr-notices' })">QR Notices</VBtn>
        <VBtn @click="router.push({ name: 'messages-new' })">Create Notice</VBtn>
      </div>
    </div>
    <VAlert v-if="error" type="error" closable class="mb-4" @click:close="error = ''">{{ error }}</VAlert>
    <AppDataTable :columns="columns" :rows="rows" :loading="loading" row-key="id" searchable search-placeholder="Search messages…" empty-text="No messages yet.">
      <template #cell-actions="{ row }">
        <VBtn size="small" variant="text" @click.stop="openView(row)">View</VBtn>
        <VBtn size="small" variant="text" @click.stop="openAnalytics(row)">Analytics</VBtn>
        <VBtn size="small" variant="text" @click.stop="router.push({ name: 'messages-edit', params: { id: row.id } })">Edit</VBtn>
        <VBtn size="small" color="error" variant="text" @click.stop="askDelete(row)">Delete</VBtn>
      </template>
    </AppDataTable>
    <ConfirmDialog :model-value="pendingDelete !== null" title="Delete message?" :message="`Permanently delete ${pendingDelete?.title || 'this message'}?`" :loading="deleting" @update:model-value="pendingDelete = null" @confirm="confirmDelete" />

    <!-- View Dialog -->
    <VDialog v-model="viewDialog" max-width="440" scrollable>
      <VCard>
        <VCardTitle class="pt-4 px-4">{{ viewItem?.title ?? 'Message' }}</VCardTitle>
        <VCardText>
          <div v-if="viewLoading" class="d-flex justify-center pa-4"><VProgressCircular indeterminate /></div>
          <div v-else-if="viewItem">
            <VImg v-if="viewItem.imgUrl" :src="viewItem.imgUrl" class="mb-3 rounded" />
            <div class="text-caption text-disabled mb-2">{{ viewItem.startDate }}</div>
            <div class="text-body-2" v-html="viewItem.content"></div>
          </div>
          <div v-else class="text-disabled pa-4 text-center">Couldn't load message.</div>
        </VCardText>
        <VCardActions class="px-4 pb-4 gap-2">
          <VBtn v-if="viewItem?.btnTitle" variant="tonal" @click="openExternalUrl(viewItem.redirectUrl)">{{ viewItem.btnTitle }}</VBtn>
          <VSpacer />
          <VBtn @click="viewDialog = false">Close</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Analytics Dialog -->
    <VDialog v-model="analyticsDialog" max-width="600" scrollable>
      <VCard>
        <VCardTitle class="pt-4 px-4">Analytics — {{ analyticsItem?.title ?? '…' }}</VCardTitle>
        <VCardText>
          <div v-if="analyticsLoading" class="d-flex justify-center pa-4"><VProgressCircular indeterminate /></div>
          <div v-else-if="analyticsItem">
            <VList density="compact" lines="one">
              <VListItem title="Unique users (Read)" :subtitle="String(countReads(analyticsItem))" />
              <VListItem title="No. of users included" :subtitle="String(analyticsItem.userGuids?.length ?? '—')" />
              <VListItem title="Notification Title" :subtitle="analyticsItem.notif_title ?? '—'" />
              <VListItem title="Topic" :subtitle="Array.isArray(analyticsItem.topic) ? analyticsItem.topic.join(', ') : (analyticsItem.topic ?? '—')" />
            </VList>
            <div v-if="analyticsReadList.length" class="mt-3">
              <div class="text-subtitle-2 mb-1">Read by:</div>
              <VTable density="compact">
                <thead><tr><th>Name</th><th>Company</th><th>Department</th></tr></thead>
                <tbody>
                  <tr v-for="(u, i) in analyticsReadList" :key="i">
                    <td>{{ u.name ?? u }}</td><td>{{ u.company ?? '—' }}</td><td>{{ u.department ?? '—' }}</td>
                  </tr>
                </tbody>
              </VTable>
            </div>
            <div v-else class="text-disabled mt-3">No read data available.</div>
          </div>
        </VCardText>
        <VCardActions class="px-4 pb-4 justify-end">
          <VBtn @click="analyticsDialog = false">Close</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>
